import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { APP_NAME } from "@/lib/app";

export default function ResetPassword() {
  const { t } = useLang();
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [state, setState] = useState("checking"); // checking | invalid | form | done
  const [requiresSms, setRequiresSms] = useState(false);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [code, setCode] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!token) { setState("invalid"); return; }
      try {
        const { data } = await api.get(`/auth/reset-password/check?token=${encodeURIComponent(token)}`);
        if (!active) return;
        if (data.valid) { setRequiresSms(!!data.requires_sms); setState("form"); }
        else setState("invalid");
      } catch { if (active) setState("invalid"); }
    })();
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== repeat) { toast.error(t("reset_pw_mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, code: code.trim(), password });
      setState("done");
      toast.success(t("reset_done"));
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#0b1623]">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(to right,#ffffff 1px,transparent 1px),linear-gradient(to bottom,#ffffff 1px,transparent 1px)", backgroundSize: "64px 64px" }} />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full bg-sky-500/10 blur-[120px]" />
      <div className="relative z-10 min-h-screen flex items-center justify-center px-5 py-10">
        <div className="relative w-full max-w-[420px] cb-fade-up px-7 sm:px-9 py-9 rounded-3xl bg-white/[0.07] border border-white/15 backdrop-blur-2xl shadow-[0_24px_80px_-24px_rgba(0,0,0,0.75)]" data-testid="reset-card">
          <div className="mb-5 grid place-items-center">
            <span className="h-14 w-14 rounded-2xl bg-white/[0.06] border border-white/10 grid place-items-center">
              <ShieldCheck className="h-7 w-7 text-sky-400" />
            </span>
          </div>
          <h1 className="font-display font-extrabold text-[1.75rem] leading-tight tracking-tight text-white text-center">
            {t("reset_title")}
          </h1>

          {state === "checking" && (
            <p className="mt-6 text-sm text-white/55 text-center" data-testid="reset-checking">…</p>
          )}

          {state === "invalid" && (
            <div className="mt-6 text-center space-y-4">
              <p className="text-sm text-white/70" data-testid="reset-invalid-msg">{t("reset_invalid_link")}</p>
              <button onClick={() => navigate("/login")} className="text-cyan-300 hover:text-cyan-200 text-sm" data-testid="reset-back-login">{t("back_to_login")}</button>
            </div>
          )}

          {state === "done" && (
            <p className="mt-6 text-sm text-emerald-300 text-center" data-testid="reset-done-msg">{t("reset_done")}</p>
          )}

          {state === "form" && (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">{t("new_password")}</Label>
                <div className="relative">
                  <Input
                    type={show ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12 rounded-xl bg-white/[0.06] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-cyan-400 pr-12"
                    data-testid="reset-password-input"
                  />
                  <button type="button" onClick={() => setShow((v) => !v)} tabIndex={-1}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-sky-500 transition-colors" data-testid="reset-password-toggle">
                    {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-white/70 text-xs">{t("repeat_password")}</Label>
                <Input type={show ? "text" : "password"} required value={repeat}
                  onChange={(e) => setRepeat(e.target.value)} placeholder="••••••••"
                  className="h-12 rounded-xl bg-white/[0.06] border-white/15 text-white placeholder:text-white/35 focus-visible:ring-cyan-400"
                  data-testid="reset-repeat-input" />
              </div>
              {requiresSms && (
                <div className="space-y-1.5">
                  <Label className="text-white/70 text-xs">{t("reset_code")}</Label>
                  <Input inputMode="numeric" maxLength={6} required value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000"
                    className="h-12 rounded-xl bg-white/[0.06] border-white/15 text-white text-center tracking-[0.5em] text-lg placeholder:text-white/25 focus-visible:ring-cyan-400"
                    data-testid="reset-code-input" />
                </div>
              )}
              <Button type="submit" disabled={loading || password.length < 10 || (requiresSms && code.length !== 6)}
                className="w-full h-12 rounded-xl text-base font-semibold bg-sky-600 hover:bg-sky-500 text-white shadow-lg shadow-sky-900/30 group transition-all duration-150"
                data-testid="reset-submit-button">
                {loading ? t("logging_in") : t("reset_submit")}
                {!loading && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
              </Button>
              <button type="button" onClick={() => navigate("/login")} className="w-full text-center text-sm text-white/50 hover:text-white/80" data-testid="reset-cancel-button">{t("back_to_login")}</button>
            </form>
          )}

          <p className="mt-7 text-[10px] tracking-[0.42em] text-cyan-300/90 font-semibold uppercase text-center">{APP_NAME}</p>
        </div>
      </div>
    </div>
  );
}
